// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — one briefing being given and recorded (Situation room, slice CR3):
 * the Khipu player plays the briefing, and each step it shows is recorded
 * with what every globe showed (stream, frame, how old) and a picture of
 * each globe for the pack; the Decision box's text is kept as it is typed.
 * Finishing builds the record (`briefingRecord.ts`) and seals it.
 *
 * One at a time per page. Nothing leaves the browser until the briefer
 * saves the record or prints the pack.
 */

import type { BriefingHooks } from '../ui/khipuPlayerUI'
import { createBriefingRecorder, type BriefingDecision, type BriefingGlobeShown, type BriefingRecorder, type BriefingStepShown } from './briefingRecord'
import { localized, newKhipuId, type Khipu } from './khipu'
import { sealKhipu } from './khipuIntegrity'

export interface BriefingSessionHost {
  /** Play this Khipu in the player, telling these hooks what happens. */
  openPlayer(k: Khipu, hooks: BriefingHooks): void
  closePlayer(): void
  /** What each globe shows now, globe 1 first. */
  globesShown(): Promise<BriefingGlobeShown[]>
  /** A picture of each globe now, globe 1 first (null where one cannot be taken). */
  capturePictures(): Promise<Array<string | null>>
  /** The account's display name, for a record with the briefer's name. */
  userName(): string | null
  lang(): string
}

export interface BriefingResult {
  record: Khipu
  title: string
  startedAt: number
  endedAt: number
  who: string | null
  steps: Array<BriefingStepShown & { pictures: Array<string | null> }>
  decisions: BriefingDecision[]
  contentSha256: string | null
}

export interface BriefingSession {
  running(): boolean
  /** The step shown (from 1) and how many there are; null when none is running. */
  progress(): { step: number; total: number; title: string } | null
  start(template: Khipu, opts: { attributed: boolean }): void
  /** End the briefing: the sealed record and what the pack needs; null when none was running. */
  finish(): Promise<BriefingResult | null>
  /** Throw the briefing away without a record. */
  discard(): void
  /** The last finished briefing, until the next one starts. */
  last(): BriefingResult | null
  onChange(listener: () => void): () => void
}

/** How long a step settles (tiles, video frame) before its pictures are taken. */
const SETTLE_MS = 3000

export function createBriefingSession(host: BriefingSessionHost, clock: () => number = Date.now): BriefingSession {
  let current: {
    template: Khipu
    recorder: BriefingRecorder
    attributed: { name: string; enabledAt: number } | null
    pictures: Array<Array<string | null>>
    index: number
    generation: number
  } | null = null
  let lastResult: BriefingResult | null = null
  const listeners = new Set<() => void>()
  const changed = (): void => {
    for (const l of listeners) l()
  }

  function hooks(session: NonNullable<typeof current>): BriefingHooks {
    return {
      onStepShown(index, step) {
        if (current !== session) return
        session.index = index
        const gen = ++session.generation
        const title = localized(step.title, host.lang()) || `${index + 1}`
        const slot = session.recorder.shown().length
        session.pictures[slot] = []
        void host.globesShown().then((globes) => {
          if (current !== session) return
          session.recorder.stepShown(step.id, title, globes)
          changed()
          window.setTimeout(() => {
            if (current !== session) return
            void host.capturePictures().then((pics) => {
              // A later showing of the same step keeps its own pictures.
              if (current === session && session.pictures[slot] && gen <= session.generation) session.pictures[slot] = pics
            })
          }, SETTLE_MS)
        })
      },
      decision: (stepId) => session.recorder.decisions().find((d) => d.stepId === stepId)?.text ?? '',
      onDecision(stepId, text) {
        if (current !== session) return
        session.recorder.decide(stepId, text)
      },
      onClose() {
        // Closing the player does not end the briefing: Finish in the panel does.
        changed()
      },
    }
  }

  return {
    running: () => current !== null,
    progress() {
      if (!current) return null
      const steps = current.template.path.steps
      const i = Math.max(0, current.index)
      return { step: i + 1, total: steps.length, title: localized(steps[i]?.title, host.lang()) }
    },
    start(template, opts) {
      const name = opts.attributed ? host.userName() : null
      const session = {
        template,
        recorder: createBriefingRecorder(template, clock),
        attributed: name ? { name, enabledAt: clock() } : null,
        pictures: [] as Array<Array<string | null>>,
        index: -1,
        generation: 0,
      }
      current = session
      lastResult = null
      host.openPlayer(template, hooks(session))
      changed()
    },
    async finish() {
      const session = current
      if (!session) return null
      current = null
      host.closePlayer()
      const now = clock()
      const lang = host.lang()
      const built = session.recorder.build({ id: newKhipuId(), now, attributed: session.attributed, lang })
      const record = await sealKhipu(built)
      const integrity = record.integrity as { contentSha256?: string } | undefined
      lastResult = {
        record,
        title: localized(record.title, lang),
        startedAt: session.recorder.startedAt(),
        endedAt: now,
        who: session.attributed?.name ?? null,
        steps: session.recorder.shown().map((s, i) => ({ ...s, pictures: session.pictures[i] ?? [] })),
        decisions: [...session.recorder.decisions()],
        contentSha256: integrity?.contentSha256 ?? null,
      }
      changed()
      return lastResult
    },
    discard() {
      if (!current) return
      current = null
      host.closePlayer()
      changed()
    },
    last: () => lastResult,
    onChange(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}
